import type React from "react";
import type { NavigationMenuData } from "../../../types";

import { trackHeaderLinkClickEvent } from "@homeinstead/analytics";
import { forwardRef } from "react";
import { cn } from "../../../utils/classnames";
import { Link } from "../../Link";
import {
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuListItem,
  NavigationMenuPrimitive,
  NavigationMenuTrigger,
} from "./NavigationMenuPrimitives";

/**
 * NavigationMenuBase serves as a base structure for rendering navigation menus.
 * It works in both desktop and mobile environments with responsive design.
 *
 * Appearance colors come from an ancestor `group/navbar` with
 * `data-appearance` (set on `<nav>` or the mobile drawer root).
 *
 * @param {React.ComponentPropsWithoutRef<typeof NavigationMenuPrimitive.Root>} [props] Additional props applicable to the root NavigationMenuPrimitive component.
 * @param {string} [className] Optional custom CSS classes to apply to the root element.
 * @param {NavigationMenuData[]} [menuItems=[]] An array of menu item data, where each item includes a title, optional children, and optional href.
 * @param {React.Ref<React.ComponentRef<typeof NavigationMenuPrimitive.Root>>} [ref] Forwarded ref for the menu root element.
 * @returns {React.ReactNode} A fully rendered navigation menu with support for nested menus, custom layouts, and various states.
 */
export const NavigationMenu = forwardRef<
  React.ComponentRef<typeof NavigationMenuPrimitive.Root>,
  {
    menuItems: NavigationMenuData[];
  } & React.ComponentPropsWithoutRef<typeof NavigationMenuPrimitive.Root>
>(({ className, menuItems = [], ...props }, ref) => {
  // Mobile drawer keeps text-new-beginnings-base at all breakpoints; desktop
  // default bar needs light text at xl, campaign keeps the green base color.
  const menuLabelClassName =
    "font-semibold text-lg text-new-beginnings-base xl:text-base group-data-[appearance=default]/navbar:xl:text-support-base";

  return (
    <NavigationMenuPrimitive.Root
      asChild
      className={cn(
        "relative z-10 flex h-full justify-between",
        // Default stays full-width in the flex slot beside the CTA; campaign
        // desktop menu shrink-wraps in the centered in-flow column.
        "w-full flex-1 group-data-[appearance=campaign]/navbar:w-auto group-data-[appearance=campaign]/navbar:flex-none",
        className,
      )}
      ref={ref}
      {...props}
    >
      <NavigationMenuList
        className={cn(
          "flex w-full flex-col items-start gap-6 pb-32 sm:pb-32 xl:h-full xl:flex-row xl:items-center xl:gap-0 xl:pb-0",
        )}
      >
        {menuItems.map((item: NavigationMenuData) => (
          <NavigationMenuItem key={item.title}>
            {item.children ? (
              <>
                <NavigationMenuTrigger>
                  <span className={menuLabelClassName}>{item.title}</span>
                </NavigationMenuTrigger>
                <NavigationMenuContent
                  className={cn(
                    "mb-6 ml-4 w-full drop-shadow-xl xl:mb-0 xl:ml-0 xl:bg-white",
                  )}
                >
                  <ul className="flex w-full flex-col">
                    {item.children.map((child) => (
                      <NavigationMenuListItem
                        className="text-md hover:bg-new-beginnings-base hover:text-support-tints-400 focus:bg-new-beginnings-base focus:text-support-tints-400"
                        href={child.href}
                        key={child.title}
                        onClick={() => {
                          trackHeaderLinkClickEvent({
                            link_title: child.title,
                            target_url: child.href,
                          });
                        }}
                      >
                        {child.title}
                      </NavigationMenuListItem>
                    ))}
                  </ul>
                </NavigationMenuContent>
              </>
            ) : (
              <NavigationMenuTrigger hasChevron={false}>
                <NavigationMenuLink asChild>
                  <Link
                    className="!no-underline"
                    href={item?.href ?? "#"}
                    onClick={() => {
                      trackHeaderLinkClickEvent({
                        link_title: item.title,
                        target_url: item.href,
                      });
                    }}
                    variant="plain"
                  >
                    <span className={menuLabelClassName}>{item.title}</span>
                  </Link>
                </NavigationMenuLink>
              </NavigationMenuTrigger>
            )}
          </NavigationMenuItem>
        ))}
      </NavigationMenuList>
    </NavigationMenuPrimitive.Root>
  );
});
NavigationMenu.displayName = NavigationMenuPrimitive.Root.displayName;
